import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PasswordBypass = ({ settings, onBypassSuccess }) => {
  const [password, setPassword] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!password?.trim()) {
      setError('Please enter a password');
      return;
    }

    setIsChecking(true);
    setError('');

    try {
      // Simulate checking delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (password === settings?.bypass_password) {
        onBypassSuccess();
      } else {
        setError('Invalid password. Please try again.');
        setPassword('');
      }
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  // Don't show bypass form if no password is set
  if (!settings?.bypass_password) {
    return (
      <div className="w-full max-w-sm space-y-4">
        <div className="text-center p-6 bg-muted/30 rounded-lg border border-border">
          <Icon name="Lock" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Access Restricted</h3>
          <p className="text-sm text-muted-foreground">
            This site is currently in development. Please check back soon!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Key" size={32} className="text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Access Pass
        </h3>
        <p className="text-sm text-muted-foreground">
          Enter the bypass password to access the site during development
        </p>
      </div>
      {/* Password Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="password" className="sr-only">
            Bypass Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e?.target?.value);
                setError(''); // Clear error when user types
              }}
              placeholder="Enter password"
              className="w-full px-4 py-3 pr-12 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={isChecking}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              disabled={isChecking}
            >
              <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isChecking || !password?.trim()}
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isChecking ? (
            <>
              <div className="w-4 h-4 border-2 border-primary-foreground/20 border-t-primary-foreground rounded-full animate-spin"></div>
              <span>Verifying...</span>
            </>
          ) : (
            <>
              <Icon name="Unlock" size={18} />
              <span>Access Site</span>
            </>
          )}
        </button>
      </form>
      {/* Help Text */}
      <div className="text-center">
        <p className="text-xs text-muted-foreground">
          Don't have the password? Contact the administrator for access.
        </p>
      </div>
    </div>
  );
};

export default PasswordBypass;